import { action, observable } from 'mobx';
import { HeapTrackEventsType } from '../types';
import { WishListTypes } from './wishlist-types';
import {
  addItemToWishlistFromURLparam,
  addToWishlistCall,
  getProductIdsFromWishlistData,
  removeFromWishlistCall,
} from './wishlistUtils';
import { InitialUrlSearchParams } from '../../../utils/environment';
import { checkIfCustomer, IS_NATIVE_APP_CLIENT } from '../../../utils/helpers';
import { fireGA4AddOrRemoveWishlistEvent } from 'src/scripts/utils/gtm-data-layer';
import { IVariant } from '../../product/types/PDP';
import { aloApiAuthFetch } from 'src/scripts/utils/alo-api-fetch';
import { ShippingCountryState } from 'src/scripts/utils/shipping';
import { currentPathEquals } from '../../../utils/localization';

export const wishlistState: WishlistState = observable({
  data: null as null | WishListTypes[],
  error: null as null | string,
  productIds: [] as number[],
  async populateFullData() {
    if (!checkIfCustomer()) {
      this.error = 'unauthorized';
    } else {
      try {
        const response = await fetchWishlist({ graphql: true });
        const isError = response.hasOwnProperty('detail');
        if (isError) {
          // [REVISIT] More robust error handling
          console.log('Wishlist fetch failed: ', response);
          this.error = (response as FetchError).detail;
        } else {
          this.data = response as WishListTypes[];
          this.productIds = getProductIdsFromWishlistData(response as WishListTypes[]);
          this.error = null;
        }
      } catch (error) {
        console.log('Error fetching wishlist:', error);
        this.error = error instanceof Error ? error.message : 'Failed to load wishlist';
      }
    }
  },
  async populateIds() {
    if (!checkIfCustomer()) {
      this.error = 'unauthorized';
    } else {
      try {
        const response = await fetchWishlist();
        if (!response) {
          console.log('error fetching a wishlist', { response });
          this.error = 'error fetching a wishlist';
          return;
        }
        const isError = response.hasOwnProperty('detail');
        if (isError) {
          console.log('Wishlist fetch failed: ', response);
          this.error = (response as FetchError).detail;
        } else {
          this.productIds = Object.keys(response as Record<number, string>).map((key) => Number(key));
          this.error = null;
        }
      } catch (error) {
        console.log('Error fetching wishlist:', error);
        this.error = error instanceof Error ? error.message : 'Failed to load wishlist';
      }
    }
  },
  async clearState() {
    this.data = null;
    this.error = null;
    this.productIds = [];
  },
});

const setWishlistProductIds = action((productIds: number[]) => {
  wishlistState.productIds = productIds;
});

const addProductIdToState = (productId: number) => {
  setWishlistProductIds([...wishlistState.productIds, productId]);
};

const removeProductIdFromState = (productId: number) => {
  const newState = wishlistState.productIds.filter((id) => id !== productId);
  setWishlistProductIds(newState);
};

const setWishlistProducts = action((products: WishListTypes[]) => {
  wishlistState.data = products;
});

const removeProductFromState = (productId: number) => {
  const newState = wishlistState.data && wishlistState.data.filter((product) => product.product_gid !== productId);
  newState && setWishlistProducts(newState);
};

const findProductFromState = (productId: number) => {
  return wishlistState.data && wishlistState.data.find((product) => product.product_gid === productId);
};

export const addItemToWishlist = async (
  productData: WishlistGA4ProductData,
  // ? [REVISIT] more strongly typed properties for this event
  heapEventProperties: HeapTrackEventsType['heapEventProperties']
) => {
  const productId = productData?.id;
  // Optimistic UI: change the icon state right away and change back if something went wrong
  addProductIdToState(Number(productId));
  const wishListResponse = await addToWishlistCall(productId || '', heapEventProperties);
  if (wishListResponse.ok && wishListResponse.status === 200) {
    fireGA4AddOrRemoveWishlistEvent(productData, { isWishlisted: false });
    if (window.sessionStorage.getItem('wishlist_product_data')) {
      window.sessionStorage.removeItem('wishlist_product_data');
    }
  } else if (!wishListResponse.ok && wishListResponse.status !== 200 && !wishListResponse.alreadyAdded) {
    removeProductIdFromState(Number(productId));
    console.log('something went wrong with adding product to wishlist: ', wishListResponse);
  }
  return wishListResponse;
};

export const removeItemFromWishlist = async (
  productData: WishlistGA4ProductData,
  heapEventProperties: HeapTrackEventsType['heapEventProperties'] & { source: string }
) => {
  const productId = productData?.id || '';
  const { source } = heapEventProperties;
  const removedProductCopy = findProductFromState(Number(productId));
  if (source === 'Wishlist' && wishlistState.data) removeProductFromState(Number(productId));
  // Optimistic UI: change the icon state right away and change back if something went wrong
  removeProductIdFromState(Number(productId));
  const wishListResponse = await removeFromWishlistCall(productId || '', heapEventProperties);
  if (wishListResponse.ok && wishListResponse.status === 200) {
    if (productData) {
      fireGA4AddOrRemoveWishlistEvent(productData, { isWishlisted: true });
      window.sessionStorage.removeItem('wishlist_product_data');
    }
  } else {
    if (source === 'Wishlist' && removedProductCopy && wishlistState.data)
      setWishlistProducts([removedProductCopy, ...wishlistState.data]);
    addProductIdToState(Number(productId));
    console.log('something went wrong with removing product from wishlist: ', wishListResponse);
  }
};

const fetchWishlist = async (options: { graphql?: boolean } = {}) => {
  if (!IS_NATIVE_APP_CLIENT) {
    const { graphql } = options;
    const countryCode = ShippingCountryState.countryCode;
    const shopifyMarketSupportedRoute = countryCode
      ? `/v1/wishlist/lists/default/items/graphql?country_code=${countryCode}`
      : '/v1/wishlist/lists/default/items/graphql';
    const endpoint = graphql ? shopifyMarketSupportedRoute : '/v1/wishlist/items';

    try {
      const response = await aloApiAuthFetch(endpoint, {
        method: graphql ? 'POST' : 'GET',
      });

      if (!response.ok) {
        throw new Error(`Failed to load wishlist. Status: ${response.status}`);
      }

      return response.json();
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Unable to load Wishlist Items!');
      }
      throw error;
    }
  }
};

wishlistState.populateIds().then(() => {
  const productAddToWishlistSourceOnLoad = InitialUrlSearchParams?.get('addToWishlistSource');
  if (checkIfCustomer() && productAddToWishlistSourceOnLoad && !currentPathEquals('/cart')) {
    addItemToWishlistFromURLparam({
      source: productAddToWishlistSourceOnLoad,
      'Wishlist Items Count': wishlistState.productIds.length + 1,
    });
  }
});

interface FetchError {
  detail: string;
}

interface WishlistState {
  data: WishListTypes[] | null;
  error: string | null;
  productIds: number[];
  populateFullData(): Promise<void>;
  populateIds(): Promise<void>;
  clearState(): void;
}

export interface WishlistGA4Options {
  isWishlisted: boolean;
}
export interface WishlistGA4ProductData {
  variantTitle: string | undefined;
  id: string;
  title: string | undefined;
  productType: string;
  price: number | undefined;
  displayPrice?: number | undefined;
  discount?: number | undefined;
  variants?: IVariant[];
  source: string;
  key?: string;
  index?: number;
  collectionHandle?: string;
  collectionShopifyId?: string;
}
